import { streamKey, type useStreamStore } from '../store/stream'
import { useThreadTitleFlash } from '../store/threadTitle'
import { useToastStore } from '../store/toast'
import type { HubEvent } from '../types/api'

/**
 * Pure hub-event router: maps one `HubEvent` to stream-store mutations and
 * React-Query invalidations. Deliberately free of socket/reconnect concerns so
 * it is unit-testable without a connection (see `__tests__/eventRouter.test.ts`).
 *
 * The stream-store state is passed in rather than imported at call time so
 * tests can drive a freshly reset store; production callers pass
 * `useStreamStore.getState()`.
 */

export type StreamStore = ReturnType<typeof useStreamStore.getState>

/**
 * The query-client surface the router needs. Declared as an explicit
 * interface (bivariant method syntax) so both the real QueryClient and test
 * fakes satisfy it structurally.
 */
export interface QueryClientLike {
  invalidateQueries(filters?: { queryKey?: unknown }): void
  setQueryData(queryKey: unknown, updater: unknown): void
}

interface WsEventDeps {
  store: StreamStore
  queryClient: QueryClientLike
  /** Timestamp used for activity-log entries and route markers. */
  ts: number
}

// ---------------------------------------------------------------------------
// isResourceMutatingTool — does this tool change resource state?
// ---------------------------------------------------------------------------

/**
 * Tools whose completion should trigger a refetch of resource queries. When an
 * agent creates, updates, deletes, or modifies a resource (including todo lists
 * and notes), the UI must refresh — there's no dedicated WS event for resource
 * changes, so we piggyback on `tool_finish`.
 */
const RESOURCE_MUTATING_TOOLS = new Set([
  'resource_create',
  'resource_update',
  'resource_delete',
  'note_append',
  'todo_add',
  'todo_complete',
  'todo_remove',
  'todo_edit',
  'todo_clear_completed',
  'plan_submit',
  'question_ask',
])

function isResourceMutatingTool(name: string): boolean {
  return RESOURCE_MUTATING_TOOLS.has(name)
}

// ---------------------------------------------------------------------------
// handleChunkEvent — content_delta accumulation
// ---------------------------------------------------------------------------

function handleChunkEvent(
  event: HubEvent,
  d: Record<string, unknown>,
  store: StreamStore,
  ts: number,
) {
  const {
    appendDelta,
    appendSubAgentDelta,
    appendActivity,
    ephemeralAgentParents,
  } = store
  const agentId = String(d.agent_id ?? '')
  const text = String(d.text ?? '')
  // Live deltas route to a thread-scoped key so the thread panel and main
  // channel don't cross-render; the activity log stays topic-scoped.
  const key = streamKey(
    event.topic_id,
    d.thread_id ? String(d.thread_id) : null,
  )
  const parent = ephemeralAgentParents[event.topic_id]?.[agentId]
  if (parent) {
    appendActivity(event.topic_id, parent, {
      type: 'content_chunk',
      agentId: parent,
      ts,
      data: { text, source_agent_id: agentId },
    })
    appendSubAgentDelta(event.topic_id, agentId, text)
  } else {
    appendDelta(key, agentId, text)
    // Also route into the topic-scoped activity log so the thread panel can
    // render an ordered timeline (content interleaved with thinking + tool
    // calls) instead of a bucket detached from the tool stream. thread_id is
    // recorded so the timeline can be scoped to the right thread.
    appendActivity(event.topic_id, agentId, {
      type: 'content_chunk',
      agentId,
      ts,
      data: { text, thread_id: d.thread_id ? String(d.thread_id) : null },
    })
  }
}

// ---------------------------------------------------------------------------
// handleThinkingEvent — thinking_delta (reasoning content)
// ---------------------------------------------------------------------------

function handleThinkingEvent(
  event: HubEvent,
  d: Record<string, unknown>,
  store: StreamStore,
  ts: number,
) {
  const { appendThinking, appendActivity, ephemeralAgentParents } = store
  const agentId = String(d.agent_id ?? '')
  const text = String(d.text ?? '')
  // Like content deltas: thread-scoped for inline display; the activity log
  // stays topic-scoped.
  const key = streamKey(
    event.topic_id,
    d.thread_id ? String(d.thread_id) : null,
  )
  const parent = ephemeralAgentParents[event.topic_id]?.[agentId]
  if (parent) {
    // Sub-agent reasoning → parent's activity feed (compact, collapsible).
    appendActivity(event.topic_id, parent, {
      type: 'thinking_chunk',
      agentId: parent,
      ts,
      data: { text, source_agent_id: agentId },
    })
  } else {
    appendThinking(key, agentId, text)
    // Mirror the content path: route into the activity log so reasoning
    // interleaves with output + tools in the live timeline. See
    // handleChunkEvent for the same dual-write rationale.
    appendActivity(event.topic_id, agentId, {
      type: 'thinking_chunk',
      agentId,
      ts,
      data: { text, thread_id: d.thread_id ? String(d.thread_id) : null },
    })
  }
}

// ---------------------------------------------------------------------------
// handleToolEvent — tool_start, tool_finish, tool_progress
// ---------------------------------------------------------------------------

function handleToolEvent(
  event: HubEvent,
  d: Record<string, unknown>,
  store: StreamStore,
  ts: number,
) {
  const { setToolActive, appendActivity, ephemeralAgentParents } = store
  const agentId = String(d.agent_id ?? '')
  // Tool activity shown in the streaming view is thread-scoped; the persistent
  // activity log stays topic-scoped.
  const key = streamKey(
    event.topic_id,
    d.thread_id ? String(d.thread_id) : null,
  )
  const parent = ephemeralAgentParents[event.topic_id]?.[agentId]

  switch (event.event_type) {
    case 'tool_start': {
      if (parent) {
        appendActivity(event.topic_id, parent, {
          type: 'tool_start',
          agentId: parent,
          ts,
          data: d,
        })
      } else {
        setToolActive(key, agentId, String(d.name ?? ''), true)
        appendActivity(event.topic_id, agentId, {
          type: 'tool_start',
          agentId,
          ts,
          data: d,
        })
      }
      break
    }
    case 'tool_finish': {
      store.clearToolProgress(key, agentId)
      if (parent) {
        appendActivity(event.topic_id, parent, {
          type: 'tool_finish',
          agentId: parent,
          ts,
          data: d,
        })
      } else {
        setToolActive(key, agentId, String(d.name ?? ''), false)
        appendActivity(event.topic_id, agentId, {
          type: 'tool_finish',
          agentId,
          ts,
          data: d,
        })
      }
      break
    }
    case 'tool_progress': {
      const line = String(d.line ?? '')
      const target = parent ?? agentId
      store.setToolProgress(key, agentId, line)
      if (target) {
        appendActivity(event.topic_id, target, {
          type: 'tool_progress',
          agentId: target,
          ts,
          data: d,
        })
      }
      break
    }
  }
}

// ---------------------------------------------------------------------------
// invalidateTopicViews — refresh topic + thread views for a topic
// ---------------------------------------------------------------------------

function invalidateTopicViews(
  queryClient: QueryClientLike,
  topicId: string,
  threadId: string | undefined,
) {
  void queryClient.invalidateQueries({ queryKey: ['topics'] })
  if (threadId) {
    void queryClient.invalidateQueries({
      queryKey: ['thread', topicId, threadId],
    })
    void queryClient.invalidateQueries({ queryKey: ['threads', topicId] })
  }
}

// ---------------------------------------------------------------------------
// handleUserMessageEvent — user_message (a human posted to the topic)
// ---------------------------------------------------------------------------

function handleUserMessageEvent(
  event: HubEvent,
  d: Record<string, unknown>,
  queryClient: QueryClientLike,
) {
  // Refresh history so other participants see the message live. Unlike
  // message_complete, do NOT clear streaming state — a user message must not
  // wipe an agent's in-flight turn. The sender's optimistic update is replaced
  // by the persisted message on refetch (same content, no duplicate).
  const threadId = d.thread_id ? String(d.thread_id) : undefined
  invalidateTopicViews(queryClient, event.topic_id, threadId)
}

// ---------------------------------------------------------------------------
// handleTurnEvent — message_complete
// ---------------------------------------------------------------------------

function handleTurnEvent(
  event: HubEvent,
  d: Record<string, unknown>,
  store: StreamStore,
  ts: number,
  queryClient: QueryClientLike,
) {
  const { clearTopic, appendActivity, ephemeralAgentParents, setTurnActive } =
    store
  const agentId = d.agent_id ? String(d.agent_id) : ''
  const parent = agentId
    ? ephemeralAgentParents[event.topic_id]?.[agentId]
    : undefined
  const logTarget = parent ?? agentId
  if (logTarget) {
    appendActivity(event.topic_id, logTarget, {
      type: 'message_complete',
      agentId: logTarget,
      ts,
      data: d,
    })
  }
  if (parent) {
    // Ephemeral sub-agent: don't clear topic (would wipe parent's live
    // state) and don't invalidate history (sub-agent message is internal
    // and filtered client-side; the parent's own message_complete will
    // trigger the final history refresh).
    void queryClient.invalidateQueries({ queryKey: ['topics'] })
  } else {
    const threadId = d.thread_id ? String(d.thread_id) : undefined
    // Clear the live stream for this turn's key (thread-scoped if it ran in a
    // thread), then refresh history and (if thread-scoped) the thread view +
    // reply counts.
    clearTopic(streamKey(event.topic_id, threadId))
    // The turn is over — clear this agent's presence so the working
    // indicator drops. (turn_ended also does this; both are safe.)
    if (agentId) setTurnActive(event.topic_id, threadId, agentId, false)
    // If the response was truncated by the output token limit, mark the key so
    // the view can show a "Continue" affordance — never let a cut-off reply
    // look like a complete one.
    if (d.truncated === true) {
      store.markTruncated(event.topic_id, threadId)
    }
    invalidateTopicViews(queryClient, event.topic_id, threadId)
  }
}

// ---------------------------------------------------------------------------
// handleAgentErrorEvent — agent_error (ephemeral; never persisted to history)
// ---------------------------------------------------------------------------

function handleAgentErrorEvent(
  event: HubEvent,
  d: Record<string, unknown>,
  store: StreamStore,
  ts: number,
) {
  const { appendActivity, ephemeralAgentParents, markError, setTurnActive } =
    store
  const agentId = d.agent_id ? String(d.agent_id) : ''
  const parent = agentId
    ? ephemeralAgentParents[event.topic_id]?.[agentId]
    : undefined
  const logTarget = parent ?? agentId
  const message = d.message ? String(d.message) : 'Agent turn failed'
  if (logTarget) {
    appendActivity(event.topic_id, logTarget, {
      type: 'agent_error',
      agentId: logTarget,
      ts,
      data: d,
    })
  }
  // Signal the topic view to clear any "waiting" indicator: when an agent fails
  // (or was never reachable) no reply is coming, so the spinner must stop.
  markError(event.topic_id, ts)
  // A failed turn is still a *ended* turn — clear presence. (turn_ended also
  // does this; both are safe.)
  if (agentId && !parent) {
    const threadId = d.thread_id ? String(d.thread_id) : undefined
    setTurnActive(event.topic_id, threadId, agentId, false)
  }
  // Surface a toast; the following message_complete clears streaming state and
  // history. This event is never written to conversation history. Replayed
  // errors (cold-connect window) already toasted on their original delivery.
  if (!(event as { _replay?: boolean })._replay) {
    useToastStore.getState().addToast(message, 'error')
  }
}

// ---------------------------------------------------------------------------
// handleSubAgentEvent — sub_agent_spawn, sub_agent_complete, sub_agent_cancel
// ---------------------------------------------------------------------------

function handleSubAgentEvent(
  event: HubEvent,
  d: Record<string, unknown>,
  store: StreamStore,
  ts: number,
) {
  const {
    setSubAgentActive,
    clearSubAgentDelta,
    addKnownPersistentSubAgent,
    recordEphemeralAgent,
    appendActivity,
  } = store

  switch (event.event_type) {
    case 'sub_agent_spawn': {
      const agentId = String(d.agent_id ?? '')
      const taskDescription = String(d.task_description ?? '')
      const persistentName = d.persistent_name
        ? String(d.persistent_name)
        : undefined
      const parentAgentId = d.parent_agent_id
        ? String(d.parent_agent_id)
        : undefined
      setSubAgentActive(
        event.topic_id,
        agentId,
        taskDescription,
        true,
        persistentName,
        parentAgentId,
      )
      if (persistentName) {
        addKnownPersistentSubAgent(event.topic_id, {
          agent_id: agentId,
          task_description: taskDescription,
          persistent_name: persistentName,
          parent_agent_id: parentAgentId,
        })
      }
      // Register all sub-agents (both ephemeral and persistent) so content_delta
      // events are routed to the activity feed instead of the main chat view.
      if (parentAgentId) {
        recordEphemeralAgent(event.topic_id, agentId, parentAgentId)
      }
      // Log spawn against the parent so the parent's feed shows what it delegated
      const logTarget = parentAgentId ?? agentId
      appendActivity(event.topic_id, logTarget, {
        type: 'sub_agent_spawn',
        agentId: logTarget,
        ts,
        data: d,
      })
      break
    }
    case 'sub_agent_complete': {
      const agentId = String(d.agent_id ?? '')
      const parentAgentId = d.parent_agent_id
        ? String(d.parent_agent_id)
        : undefined
      setSubAgentActive(event.topic_id, agentId, '', false)
      clearSubAgentDelta(event.topic_id, agentId)
      const logTarget = parentAgentId ?? agentId
      appendActivity(event.topic_id, logTarget, {
        type: 'sub_agent_complete',
        agentId: logTarget,
        ts,
        data: d,
      })
      break
    }
    case 'sub_agent_cancel': {
      const agentId = String(d.agent_id ?? '')
      const parentAgentId = d.parent_agent_id
        ? String(d.parent_agent_id)
        : undefined
      setSubAgentActive(event.topic_id, agentId, '', false)
      clearSubAgentDelta(event.topic_id, agentId)
      const logTarget = parentAgentId ?? agentId
      appendActivity(event.topic_id, logTarget, {
        type: 'sub_agent_cancel',
        agentId: logTarget,
        ts,
        data: d,
      })
      break
    }
  }
}

// ---------------------------------------------------------------------------
// handleReviewEvent — plan/question lifecycle
// ---------------------------------------------------------------------------

/**
 * Plan and question reviews are rendered inline in their birth thread: the
 * card attaches to the blocking tool call (live turn timeline + historical
 * tool row), so their events refresh the affected views: the thread
 * transcript, the thread list (waiting_on pill), and resource caches (the
 * card body renders from a live resource fetch, so a decision flips it via
 * refetch).
 *
 * On `*_ready` we also append a `resource_card` activity entry so the live
 * timeline shows the interactive card below the running tool call — the
 * interaction happens *during* the blocked call, not before the turn.
 */
function handleReviewEvent(
  event: HubEvent,
  d: Record<string, unknown>,
  queryClient: QueryClientLike,
  store: StreamStore,
  ts: number,
) {
  const threadId = d.thread_id ? String(d.thread_id) : undefined
  invalidateTopicViews(queryClient, event.topic_id, threadId)
  // ['resource'] covers the per-card query family (['resource','card',…]) —
  // a decision made elsewhere (other client, timeout) must flip open cards.
  void queryClient.invalidateQueries({ queryKey: ['resource'] })
  void queryClient.invalidateQueries({ queryKey: ['resources'] })
  void queryClient.invalidateQueries({
    queryKey: ['thread-resources', event.topic_id],
  })
  // Every review-lifecycle transition can move an item in or out of the
  // workspace-wide waiting list (ready → added, decided → removed).
  void queryClient.invalidateQueries({ queryKey: ['waiting'] })

  if (
    event.event_type !== 'plan_ready' &&
    event.event_type !== 'question_ready'
  )
    return
  const agentId = d.agent_id ? String(d.agent_id) : ''
  if (!agentId || !threadId) return
  const resourceId = d.resource_id
    ? String(d.resource_id)
    : d.plan_id
      ? String(d.plan_id)
      : d.question_id
        ? String(d.question_id)
        : ''
  if (!resourceId) return
  const kind = event.event_type === 'plan_ready' ? 'plan' : 'question'
  const text = d.plan_text ?? d.question ?? ''
  const title = String(text)
    .split('\n')
    .find((l: string) => l.trim() !== '')
    ?.slice(0, 80)
  store.appendActivity(event.topic_id, agentId, {
    type: 'resource_card',
    agentId,
    ts,
    data: {
      thread_id: threadId,
      resource_id: resourceId,
      kind,
      ...(title ? { title } : {}),
    },
  })
}

// ---------------------------------------------------------------------------
// routeHubEvent — one event in, store mutations + invalidations out
// ---------------------------------------------------------------------------

export function routeHubEvent(event: HubEvent, deps: WsEventDeps): void {
  const { store, queryClient } = deps
  const d = event.data as Record<string, unknown>
  const ts = deps.ts

  switch (event.event_type) {
    case 'content_delta':
      handleChunkEvent(event, d, store, ts)
      break
    case 'thinking_delta':
      handleThinkingEvent(event, d, store, ts)
      break
    case 'tool_start':
    case 'tool_finish':
    case 'tool_progress':
      handleToolEvent(event, d, store, ts)
      // Resource-mutating tools change DB state that the UI caches via
      // React Query. There's no dedicated WS event for agent-side resource
      // changes, so invalidate resource queries here to refetch
      // immediately. plan_submit/question_ask also feed the waiting list.
      if (
        event.event_type === 'tool_finish' &&
        isResourceMutatingTool(String(d.name ?? ''))
      ) {
        void queryClient.invalidateQueries({ queryKey: ['resources'] })
        void queryClient.invalidateQueries({ queryKey: ['waiting'] })
      }
      break
    case 'turn_started': {
      const agentId = d.agent_id ? String(d.agent_id) : ''
      if (agentId) {
        const threadId = d.thread_id ? String(d.thread_id) : undefined
        store.setTurnActive(event.topic_id, threadId, agentId, true)
      }
      break
    }
    case 'turn_ended': {
      const agentId = d.agent_id ? String(d.agent_id) : ''
      if (agentId) {
        const threadId = d.thread_id ? String(d.thread_id) : undefined
        store.setTurnActive(event.topic_id, threadId, agentId, false)
      }
      break
    }
    case 'message_complete':
      handleTurnEvent(event, d, store, ts, queryClient)
      break
    case 'user_message':
      handleUserMessageEvent(event, d, queryClient)
      break
    case 'agent_error':
      handleAgentErrorEvent(event, d, store, ts)
      break
    case 'sub_agent_spawn':
    case 'sub_agent_complete':
    case 'sub_agent_cancel':
      handleSubAgentEvent(event, d, store, ts)
      break
    case 'plan_ready':
    case 'plan_approved':
    case 'plan_rejected':
    case 'question_ready':
    case 'question_answered':
      handleReviewEvent(event, d, queryClient, store, ts)
      break
    case 'resource_changed': {
      // A web-layer resource mutation (create / update / rename / delete /
      // promote). Refreshes every resources view; the waiting list rides
      // along because plan/question rows are resources. Agent-side
      // mutations still surface via the tool_finish piggyback above.
      void queryClient.invalidateQueries({ queryKey: ['resource'] })
      void queryClient.invalidateQueries({ queryKey: ['resources'] })
      void queryClient.invalidateQueries({
        queryKey: ['thread-resources', event.topic_id],
      })
      void queryClient.invalidateQueries({ queryKey: ['waiting'] })
      break
    }
    case 'topic_event': {
      // A cron/timer/webhook firing (or a router decision — see below).
      // It's persisted in topic_events and returned by the history
      // endpoint; refetch so it lands in the timeline.
      invalidateTopicViews(queryClient, event.topic_id, undefined)
      const kind = d.kind ? String(d.kind) : ''
      if (kind === 'route.selected' || kind === 'route.fallback') {
        const payload = (d.payload ?? {}) as Record<string, unknown>
        store.setLastRoute(event.topic_id, {
          fallback: kind === 'route.fallback',
          agentId: payload.agent_id ? String(payload.agent_id) : undefined,
          model: payload.model ? String(payload.model) : undefined,
          candidates: Array.isArray(payload.candidates)
            ? payload.candidates.map(String)
            : [],
          reason: payload.reason ? String(payload.reason) : undefined,
          ts,
        })
      }
      break
    }
    case 'user_presence': {
      // Replayed events reflect historical presence state from before the
      // connection's fresh init snapshot; ignore them so a reconnect doesn't
      // falsely overwrite current online status.
      if ((event as { _replay?: boolean })._replay) break
      const wsId = d.workspace_id ? String(d.workspace_id) : ''
      const userId = d.user_id ? String(d.user_id) : ''
      const online = d.online === true
      if (wsId && userId) store.setUserOnline(wsId, userId, online)
      break
    }
    case 'agent_presence': {
      if ((event as { _replay?: boolean })._replay) break
      const agentId = d.agent_id ? String(d.agent_id) : ''
      const online = d.online === true
      if (agentId) store.setAgentOnline(agentId, online)
      break
    }
    case 'thread_retitled': {
      // Auto-title evolved (or a user pinned). Refetch the thread list +
      // open thread so every view (row, header, composer scope) updates in
      // unison, and pulse the accent flash so the change is noticed.
      const d = event.data as Record<string, unknown>
      const tid = d.thread_id ? String(d.thread_id) : undefined
      if (tid) {
        useThreadTitleFlash.getState().flash(tid)
      }
      invalidateTopicViews(queryClient, event.topic_id, tid)
      break
    }
    case 'thread_resolved':
      // open ↔ resolved transition. Refetch so the clock swaps from a live
      // elapsed timer to a settled timestamp.
      invalidateTopicViews(
        queryClient,
        event.topic_id,
        (event.data as Record<string, unknown>).thread_id
          ? String((event.data as Record<string, unknown>).thread_id)
          : undefined,
      )
      break
    case 'thread_archived':
    case 'thread_unarchived':
    case 'thread_trashed':
    case 'thread_restored':
      // Lifecycle bucket change. Refetch the list (prefix key refreshes
      // every view) and the open thread so its header actions update.
      invalidateTopicViews(
        queryClient,
        event.topic_id,
        (event.data as Record<string, unknown>).thread_id
          ? String((event.data as Record<string, unknown>).thread_id)
          : undefined,
      )
      break
    case 'thread_purged': {
      // Hard delete — the row is gone. Refresh the list (drops the row).
      // A self-initiated purge also closes the open thread from the
      // mutation handler; a remote purge of the currently-open thread is
      // left to reconcile on the next navigation.
      const purgedId = (event.data as Record<string, unknown>).thread_id
        ? String((event.data as Record<string, unknown>).thread_id)
        : undefined
      invalidateTopicViews(queryClient, event.topic_id, purgedId)
      break
    }
    default:
      break
  }
}
